import { useAtomValue } from 'jotai';

import type { Locale } from '@core/Entities/Locale/Locale.entity';
import { getFormattedCurrency } from '@core/Utils/Intl/GetFormattedCurrency';
import { mappedLocales } from '@core/Utils/Intl/MappedLocales';
import { Typography } from '@gds/Typography/Typography';

import { selectedProductIdAtom } from '../../../Atoms/SelectedProductId.atom';
import type { Calculation, CalculationProduct } from '../../../Entities/Calculation.entity';
import type { CalculatorMessages } from '../Calculator.messages';
import { totalDepositContribution } from '../Helpers/TotalContribution';

import styles from './Summary.module.css';

type SummaryUKProps = {
  calculation: Calculation;
  product: CalculationProduct;
  locale: Locale;
  summaryStrings: CalculatorMessages;
};

export const SummaryUK = ({ product, locale, calculation, summaryStrings }: SummaryUKProps) => {
  const selectedProductId = useAtomValue(selectedProductIdAtom);
  const configTwoDecimal = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  const { depositContribution, isDepositContribution, totalContribution, isTotalContribution } =
    totalDepositContribution(calculation, selectedProductId as string);

  const isPCP = selectedProductId === 'PCP';
  const isHP = selectedProductId === 'HP';

  const summaryComponent = (
    <summary className={styles.container}>
      <ul className={styles.summary}>
        <li data-id="instalments-container" className={styles.instalmentsContainer}>
          <ul className={styles.instalmentsList}>
            <li data-id="instalments" className={styles.instalments}>
              <span>
                {product.inputs.months.value} {summaryStrings.totalMonthlyRate}
              </span>

              <span className={styles.totalMonthlyRate}>
                {getFormattedCurrency(
                  product.calculationResult.totalMonthlyRate,
                  locale,
                  configTwoDecimal,
                )}
              </span>
            </li>
          </ul>
        </li>

        <li data-id="fees">
          <ul>
            <div>
              <li>
                <span>{summaryStrings.annualInterestRate}</span>
                <Typography Component="span" variant="body2">
                  {product.calculationResult.annualInterestRate}%
                </Typography>
              </li>
              <li>
                <span>{summaryStrings.nominalInterestRate}</span>
                <Typography Component="span" variant="body2">
                  {product.calculationResult.nominalInterestRate}%
                </Typography>
              </li>
            </div>

            {calculation.lender && (
              <li>
                <span>{summaryStrings.lender}</span>
                <Typography Component="span" variant="body2">
                  {calculation.lender}
                </Typography>
              </li>
            )}
          </ul>
        </li>
        <li>
          <div className={styles.divider} />
          <ul>
            {isTotalContribution && (
              <li>
                <span>{summaryStrings.totalDeposit}</span>
                <Typography Component="span" variant="body2">
                  {getFormattedCurrency(totalContribution, locale, configTwoDecimal)}
                </Typography>
              </li>
            )}
            <li>
              <span>{summaryStrings.optionalFinalPayment}</span>
              <Typography Component="span" variant="body2">
                {getFormattedCurrency(
                  product.calculationResult.optionalFinalPayment,
                  locale,
                  configTwoDecimal,
                )}
              </Typography>
            </li>

            <div>
              <li>
                <span>{summaryStrings.retailCashPrice}</span>
                <Typography Component="span" variant="body2">
                  {getFormattedCurrency(
                    product.calculationResult.totalPrice,
                    locale,
                    configTwoDecimal,
                  )}
                </Typography>
              </li>
              <li>
                <span>{summaryStrings.cashDeposit}</span>
                <Typography Component="span" variant="body2">
                  {getFormattedCurrency(
                    product.calculationResult.cashDeposit,
                    locale,
                    configTwoDecimal,
                  )}
                </Typography>
              </li>
              <li>
                <span>{summaryStrings.months}</span>
                <Typography Component="span" variant="body2">
                  {product.inputs.months.value} {summaryStrings.monthsInput}
                </Typography>
              </li>

              <li>
                <span>{summaryStrings.chargesForCredit}</span>
                <Typography Component="span" variant="body2">
                  {getFormattedCurrency(
                    product.calculationResult.chargesForCredit,
                    locale,
                    configTwoDecimal,
                  )}
                </Typography>
              </li>
              <li>
                <span>{summaryStrings.balance}</span>
                <Typography Component="span" variant="body2">
                  {getFormattedCurrency(
                    product.calculationResult.balance,
                    locale,
                    configTwoDecimal,
                  )}
                </Typography>
              </li>
              <li>
                <span>{summaryStrings.totalAmountPayable}</span>
                <Typography Component="span" variant="body2">
                  {getFormattedCurrency(
                    product.calculationResult.totalAmount,
                    locale,
                    configTwoDecimal,
                  )}
                </Typography>
              </li>

              {isDepositContribution && (
                <li>
                  <span>{summaryStrings.depositContribution}</span>
                  <Typography Component="span" variant="body2">
                    {getFormattedCurrency(depositContribution, locale, configTwoDecimal)}
                  </Typography>
                </li>
              )}
              {isPCP && (
                <>
                  <li>
                    <span>{summaryStrings.mileage}</span>
                    <Typography Component="span" variant="body2">
                      {product.inputs?.mileage?.value.toLocaleString(mappedLocales[locale])}
                    </Typography>
                  </li>
                  <li>
                    <span>{summaryStrings.excessMileagePerMile}</span>
                    <Typography Component="span" variant="body2">
                      {product.calculationResult.excessMileageRate}p
                    </Typography>
                  </li>
                </>
              )}
              {(isPCP || isHP) && (
                <li>
                  <span>{summaryStrings.optionToPurchaseFee}</span>
                  <Typography Component="span" variant="body2">
                    {getFormattedCurrency(10, locale, configTwoDecimal)}
                  </Typography>
                </li>
              )}
            </div>
          </ul>
        </li>
      </ul>
    </summary>
  );
  return summaryComponent;
};
